import { useMemo } from 'react';
import type { Employee, WorkSchedule } from '../../../types/employee';
import { formatNumber } from '../../../utils/formatters';
import { calculateWeeklyHours } from '../../../utils/payrollCalculations';

export interface SeveranceInfo {
  isEligible: boolean;
  daysUntilEligibility: number | null;
  severancePay: number | null;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const convertToDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}\.\s?\d{2}\.\s?\d{2}\.?$/.test(trimmed)) {
      const digits = trimmed.replace(/[^0-9]/g, '');
      if (digits.length === 8) {
        const year = Number(digits.slice(0, 4));
        const month = Number(digits.slice(4, 6)) - 1;
        const day = Number(digits.slice(6, 8));
        const parsed = new Date(year, month, day);
        return isNaN(parsed.getTime()) ? null : parsed;
      }
    }
    if (/^\d{4}[/-]\d{2}[/-]\d{2}$/.test(trimmed)) {
      const normalized = trimmed.replace(/\//g, '-');
      const parsed = new Date(`${normalized}T00:00:00`);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    const normalized = trimmed.length === 10 ? `${trimmed}T00:00:00` : trimmed;
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'number') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (value && typeof value === 'object') {
    if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
      const parsed = (value as { toDate: () => Date }).toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
    }
    const obj = value as { seconds?: number };
    if ('seconds' in obj && typeof obj.seconds === 'number') {
      return new Date(obj.seconds * 1000);
    }
  }
  return null;
};

const getScheduleDate = (schedule: WorkSchedule): Date | null => {
  if (!schedule.date) return null;
  return convertToDate(schedule.date);
};

const convertBreakTimeToHours = (breakTime?: string | null): number => {
  switch (breakTime) {
    case '0분': return 0;
    case '30분': return 0.5;
    case '1시간': return 1;
    case '1시간 30분': return 1.5;
    case '2시간': return 2;
    default: return 0;
  }
};

const calculateAverageDailyWage = (
  employee: Employee,
  employeeSchedules: WorkSchedule[],
  calculationDate: Date
): number | null => {
  const threeMonthsAgo = new Date(calculationDate);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  threeMonthsAgo.setHours(0, 0, 0, 0);

  const endOfCalculation = new Date(calculationDate);
  endOfCalculation.setHours(23, 59, 59, 999);

  const recentSchedules = employeeSchedules.filter((schedule) => {
    const scheduleDate = getScheduleDate(schedule);
    return (
      scheduleDate !== null &&
      scheduleDate >= threeMonthsAgo &&
      scheduleDate <= endOfCalculation
    );
  });

  if (recentSchedules.length === 0) return null;

  let totalWorkHours = 0;
  recentSchedules.forEach((schedule) => {
    const startTime = convertToDate(schedule.start_time);
    const endTimeRaw = convertToDate(schedule.end_time);
    if (!startTime || !endTimeRaw) return;

    let endTime = new Date(endTimeRaw);
    if (endTime <= startTime) endTime = new Date(endTime.getTime() + MS_PER_DAY);

    const durationMinutes = (endTime.getTime() - startTime.getTime()) / 60000;
    const breakMinutes = convertBreakTimeToHours(schedule.break_time) * 60;
    const workedMinutes = Math.max(0, durationMinutes - breakMinutes);
    totalWorkHours += workedMinutes / 60;
  });

  let totalWage = 0;
  if (employee.isHourly) {
    totalWage = totalWorkHours * (employee.hourlyRate || 0);
    if (!employee.isweeklyWork) {
      const weeklySchedules = recentSchedules;
      const weekIterator = new Date(threeMonthsAgo);
      weekIterator.setDate(weekIterator.getDate() - weekIterator.getDay());

      while (weekIterator <= endOfCalculation) {
        const weeklyHours = calculateWeeklyHours(weeklySchedules, weekIterator);
        if (weeklyHours >= 15) {
          totalWage += (weeklyHours / 40) * 8 * (employee.hourlyRate || 0);
        }
        weekIterator.setDate(weekIterator.getDate() + 7);
      }
    }
  } else {
    totalWage = (employee.monthlySalary || 0) * 3;
  }

  const totalDays = Math.max(
    1,
    Math.ceil((endOfCalculation.getTime() - threeMonthsAgo.getTime()) / MS_PER_DAY)
  );
  return totalWage / totalDays;
};

export function calculateSeveranceInfo(
  employee: Employee,
  schedules: WorkSchedule[],
  calculationDate: Date
): SeveranceInfo | null {
  const employeeSchedules = schedules.filter((schedule) => {
    if (schedule.employee_id !== employee.id) return false;
    if (employee.account_number != null && schedule.account_number != null) {
      return schedule.account_number === employee.account_number;
    }
    if (employee.accountNumber != null && schedule.account_number != null) {
      return schedule.account_number === employee.accountNumber;
    }
    return true;
  });

  let hireDate = convertToDate(employee.hire_date || employee.hireDate);
  if (!hireDate) {
    employeeSchedules.forEach((schedule) => {
      const scheduleDate = getScheduleDate(schedule);
      if (!scheduleDate) return;
      if (!hireDate || scheduleDate < hireDate) hireDate = scheduleDate;
    });
  }

  if (!hireDate) return null;

  const targetDate = new Date(calculationDate);
  if (targetDate < hireDate) return null;

  const totalWorkDays = Math.floor((targetDate.getTime() - hireDate.getTime()) / MS_PER_DAY) + 1;
  const oneYearLater = new Date(hireDate.getTime() + 365 * MS_PER_DAY);

  if (totalWorkDays < 365) {
    const daysUntilEligible = Math.max(
      0,
      Math.ceil((oneYearLater.getTime() - targetDate.getTime()) / MS_PER_DAY)
    );
    return {
      isEligible: false,
      daysUntilEligibility: daysUntilEligible,
      severancePay: null,
    };
  }

  const averageDailyWage = calculateAverageDailyWage(employee, employeeSchedules, targetDate);
  if (averageDailyWage == null) {
    return {
      isEligible: true,
      daysUntilEligibility: 0,
      severancePay: null,
    };
  }

  const severancePay = averageDailyWage * 30 * (totalWorkDays / 365);
  return {
    isEligible: true,
    daysUntilEligibility: 0,
    severancePay,
  };
}

export function renderSeveranceMessage(info: SeveranceInfo | null): string | null {
  if (!info) return null;
  if (!info.isEligible) {
    if (info.daysUntilEligibility == null) return null;
    return `퇴직금 지급까지 ${info.daysUntilEligibility}일 남았습니다.`;
  }
  if (info.severancePay != null) {
    return `예상 퇴직금: ${formatNumber(info.severancePay, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  }
  return '퇴직금을 계산할 수 있는 데이터가 부족합니다.';
}

interface UseEmployeeSpreadsheetParams {
  employees: Employee[];
  schedules: WorkSchedule[];
  searchTerm: string;
  filterStatus: 'all' | 'active' | 'retired';
}

export function useEmployeeSpreadsheet({
  employees,
  schedules,
  searchTerm,
  filterStatus,
}: UseEmployeeSpreadsheetParams) {
  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'active' && !employee.isRetired) ||
        (filterStatus === 'retired' && !!employee.isRetired);
      return matchesSearch && matchesStatus;
    });
  }, [employees, searchTerm, filterStatus]);

  const sortedEmployees = useMemo(() => {
    return [...filteredEmployees].sort((a, b) => {
      const aName = a.name || '';
      const bName = b.name || '';
      return aName.localeCompare(bName, 'ko');
    });
  }, [filteredEmployees]);

  const employeeStats = useMemo(() => {
    const active = employees.filter((e) => !e.isRetired);
    const totalCount = active.length;
    const totalMP = active.reduce((sum, e) => sum + (e.mp || 0), 0);
    const hourlyCount = active.filter((e) => e.isHourly).length;
    const salaryCount = active.filter((e) => !e.isHourly).length;

    return {
      totalCount,
      activeCount: totalCount,
      totalMP,
      hourlyCount,
      salaryCount,
    };
  }, [employees]);

  const employeeSeveranceInfo = useMemo(() => {
    const calculationDate = new Date();
    const info: Record<string, SeveranceInfo | null> = {};
    employees.forEach((emp) => {
      info[emp.id] = calculateSeveranceInfo(emp, schedules, calculationDate);
    });
    return info;
  }, [employees, schedules]);

  return {
    filteredEmployees,
    sortedEmployees,
    employeeStats,
    employeeSeveranceInfo,
  };
}
